import { type ErrorObject } from 'ajv';
import type Ajv from 'ajv';
import {
  ParseError,
  parsePhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js/max';
import Metadata from 'libphonenumber-js/metadata.max.json';
import { type DataValidateFunction } from 'ajv/dist/types';

export function addCustomKeywords(
  ajv: Ajv,
  libraryBasedPhoneFieldValidationEnabled: boolean,
  lenientPhoneValidation: boolean,
) {
  if (libraryBasedPhoneFieldValidationEnabled) {
    ajv.addKeyword({
      keyword: 'phone',
      type: 'string',
      errors: true,
      compile: () => compilePhoneValidation(lenientPhoneValidation),
    });

    ajv.addKeyword({
      keyword: 'allowedCountryCodes',
      type: 'string',
      errors: true,
      compile(schema): DataValidateFunction {
        return compileAllowedCountryCodesValidation(schema);
      },
    });
  }
}

function compilePhoneValidation(
  lenientPhoneValidation: boolean,
): DataValidateFunction {
  const validate: DataValidateFunction = (data: any): boolean => {
    const errors: Partial<ErrorObject>[] = [];
    const violation = validatePhoneNumber(data, lenientPhoneValidation);
    if (violation) {
      errors.push({
        keyword: violation.keyword,
        params: { format: 'phone' },
        message: violation.message,
      });
      validate.errors = errors as ErrorObject[];
      return false;
    }
    return true;
  };
  return validate;
}

function validatePhoneNumber(
  data: string,
  lenientPhoneValidation: Boolean,
):
  | {
      keyword: string;
      message: string;
    }
  | undefined {
  try {
    const parsedNumber = parsePhoneNumberWithError(data);
    if (
      !(lenientPhoneValidation
        ? parsedNumber.isPossible()
        : parsedNumber.isValid())
    ) {
      return {
        keyword: 'format',
        message: 'Phone number for the country code provided is invalid',
      };
    } else {
      return undefined;
    }
  } catch (e) {
    if (e instanceof ParseError) {
      return phoneValidationErrorMessages(e, data);
    } else {
      return {
        keyword: 'format',
        message: 'Phone number could not be parsed',
      };
    }
  }
}

function compileAllowedCountryCodesValidation(
  schema: any[],
): DataValidateFunction {
  const validate: DataValidateFunction = (data: any): boolean => {
    const errors: Partial<ErrorObject>[] = [];
    try {
      const phoneNumber = parsePhoneNumber(data);
      // TODO when allowedCountryCodes list is empty, replace it with a predefined default (currently it does not exist)
      if (!schema.includes(phoneNumber?.country) && schema.length > 0) {
        errors.push({
          keyword: 'allowedCountryCodes',
          params: { allowedCountryCodes: schema, format: 'phone' },
          message:
            "Phone number's country code must correspond to one from allowed countries",
        });
        validate.errors = errors as ErrorObject[];
        return false;
      }
    } catch {
      return true;
    }
    return true;
  };
  return validate;
}

function phoneValidationErrorMessages(
  e: ParseError,
  data: string,
): {
  keyword: string;
  message: string;
} {
  switch (e.message) {
    case 'INVALID_COUNTRY':
      return {
        keyword: 'countryCode',
        message: 'Phone number has incorrect country code',
      };
    case 'NOT_A_NUMBER':
      return {
        keyword: 'format',
        message: 'Text provided is not a phone number',
      };
    case 'TOO_SHORT':
      return handleTooShortCase(data);
    case 'TOO_LONG':
      return { keyword: 'format', message: 'Phone number is too long' };
    case 'INVALID_LENGTH':
    default:
      return { keyword: 'format', message: 'Phone number could not be parsed' };
  }
}

function handleTooShortCase(data: string): {
  keyword: string;
  message: string;
} {
  const callingCode = getCallingCode(data);
  if (callingCode) {
    if (onlyCallingCode(callingCode, data)) {
      return {
        keyword: 'format',
        message: 'Phone number is missing',
      };
    } else {
      return {
        keyword: 'format',
        message: 'Phone number is too short',
      };
    }
  } else {
    return {
      keyword: 'countryCode',
      message: 'Phone number has incorrect country code',
    };
  }
}

function getCallingCode(phoneNumber: string): string | undefined {
  const withoutPrefix = phoneNumber.trim().replace(/^\+/, '');

  for (let length = 4; length > 0; length--) {
    const callingCode = withoutPrefix.substring(0, length);
    const code = Metadata.country_calling_codes[callingCode]?.[0];
    if (code) {
      return callingCode;
    }
  }
  return undefined;
}

function onlyCallingCode(callingCode: string, data: string): boolean {
  return data.replace(/^\+/, '') === callingCode;
}
